import type { TokenStorageGateway } from "@shared-kernel/application/ports/token-storage-gateway";
import type { SUPPORTED_COUNTRY } from "@shared-kernel/domain/types/supported-country";
import type {
  PublicRepository,
  LoginResponseVM,
  UserBody,
  UserPasswordBody,
  ActivateAccountBody,
  PublicStudioListVM,
  AddressSuggestion,
} from "@shared-kernel/application/ports/public/public-repository";
import type { TokenValidationResult } from "@shared-kernel/application/ports/public/public-repository";
import { HttpGateway } from "@shared-kernel/secondary/http-gateway";
import config from "../../../config/backend";

export class BackendPublicRepository extends HttpGateway implements PublicRepository {
  private _publicRoute: string = "v1/public/users";
  // Auth routes live directly under v1/public (not v1/public/users).
  private _authRoute: string = "v1/public";

  constructor(tokenStorageGateway: TokenStorageGateway) {
    super(config.url, tokenStorageGateway);
  }

  // Authentication
  async login(body: UserBody): Promise<LoginResponseVM> {
    const { data } = await this._instance.post<LoginResponseVM>(this._authRoute + "/login", body);
    return data;
  }

  async logout(refreshToken: string): Promise<void> {
    await this._instance.post(this._authRoute + "/logout", { refreshToken });
  }

  async resetPassword(body: { email: string }): Promise<void> {
    const route = this._publicRoute + "/passwords/reset";
    await this._instance.post(route, body);
  }

  async updatePassword(body: UserPasswordBody): Promise<void> {
    const route = this._publicRoute + "/passwords/update";
    await this._instance.put(route, body);
  }

  async verifyTokenValidity(userId: string, token: string): Promise<TokenValidationResult> {
    const { data } = await this._instance.post(`${this._publicRoute}/token/validate`, { userId, token });
    return data;
  }

  // Account Activation
  async activateAccount(body: ActivateAccountBody): Promise<void> {
    const route = this._publicRoute + "/accounts:activate";
    await this._instance.post(route, body);
  }

  async uploadProfilePicture(file: File): Promise<string> {
    const formData = new FormData();
    formData.append("image", file);
    const { data } = await this._instance.post(`${this._publicRoute}/profile-pictures:upload`, formData);
    return data;
  }

  // Public Data
  async getPublicStudios(): Promise<PublicStudioListVM[]> {
    const { data } = await this._instance.get<PublicStudioListVM[]>("v1/public/studios");
    return data;
  }

  async searchAddresses(query: string, sessionToken: string, country: SUPPORTED_COUNTRY): Promise<AddressSuggestion[]> {
    const { data } = await this._instance.get<AddressSuggestion[]>("v1/public/addresses/search", {
      params: { query, sessionToken, country },
    });
    return data;
  }
}
